import LogoIcon from "@ch/assets/icons/pack/LogoIcon";
import useGetUnreadMessages from "@ch/helpers/hooks/useGetUnreadMessages";
import useToggleSidebarAnimation from "@ch/helpers/hooks/useToggleSidebarAnimation";
import { animated } from "@react-spring/web";
import { type FC, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useWindowSize } from "usehooks-ts";
import Button from "../Buttons/Button";
import SidebarRoutes from "./components/SidebarRoutes";

const LogoSmall = (props: React.SVGProps<SVGSVGElement>) => (
  <svg fill="none" height="73" viewBox="0 0 73 73" width="73" {...props}>
    <title>logo</title>
    <path
      d="M58.581 40.191c1.1 3.207 1.86 6.28 2.377 9.412.63 3.826.938 7.67.519 11.549-.058.534-.156.801-.782.81-10.85.157-21.066-2.012-30.18-8.244-2.876-1.967-5.441-4.285-7.61-7.038-.414-.527-.476-.992-.238-1.596.65-1.653 1.633-3.09 2.704-4.484.345-.45.545-.458.929-.023 2.258 2.558 4.709 4.915 7.452 6.954 4.881 3.63 10.235 6.17 16.276 7.125 1.344.213 2.694.3 4.056.318.675.01.822-.28.842-.85.074-2.04-.352-4.011-.885-5.95-.237-.865-.131-1.376.574-1.987 1.284-1.11 2.12-2.608 2.949-4.082.316-.562.608-1.14 1.017-1.913ZM14.278 32.769c-.83-2.32-1.401-4.538-1.873-6.78-.949-4.499-1.394-9.04-1.056-13.648.068-.915.265-1.338 1.327-1.34 10.504-.03 20.408 2.103 29.277 8.046a35.669 35.669 0 0 1 7.882 7.168c.51.628.628 1.181.302 1.94-.647 1.507-1.55 2.838-2.521 4.142-.439.59-.676.644-1.193.05-3.059-3.522-6.526-6.58-10.51-9.014-5.165-3.157-10.686-5.177-16.81-5.304-.908-.02-1.193.28-1.176 1.15.04 2.064.478 4.058 1 6.035.148.559.108.911-.375 1.321-1.658 1.413-2.7 3.296-3.71 5.19-.16.3-.325.599-.564 1.042v.002Z"
      fill="#4ABE82"
    />
    <path
      d="M32.82 58.547c-3.9 1.408-7.785 2.422-11.784 2.992-2.82.402-5.649.552-8.498.408-.814-.041-1.136-.2-1.165-1.138-.216-7.135.539-14.123 2.955-20.872 2.256-6.3 5.657-11.852 10.606-16.412 1.013-.934 1.901-1.22 3.15-.543a35.484 35.484 0 0 1 3.465 2.132c.603.422.534.683.035 1.145-3.177 2.944-5.95 6.233-8.19 9.952-3.199 5.308-5.214 10.994-5.317 17.266-.03 1.856.021 1.877 1.882 1.76 1.851-.115 3.62-.633 5.396-1.102.387-.103.713-.222 1.047.158 1.621 1.844 3.761 2.925 5.902 4 .14.07.28.139.515.253h.001ZM40.306 14.283A57.083 57.083 0 0 1 52.47 11.5c2.604-.303 5.214-.4 7.834-.207 1.454.108 1.451.074 1.459 1.495.038 7.606-1.045 15.008-4.033 22.049-2.455 5.787-5.881 10.879-10.72 14.955-.735.618-1.386.78-2.263.371-1.358-.634-2.577-1.47-3.796-2.33-.735-.518-.587-.836.002-1.361 4.127-3.674 7.56-7.915 10.098-12.846 2.357-4.582 3.805-9.418 3.873-14.627.012-.932-.344-1.183-1.188-1.174-2.122.024-4.153.555-6.192 1.044-.309.074-.59.253-.872-.096-1.654-2.05-3.975-3.16-6.365-4.489Z"
      fill="#9E9F9E"
    />
    <path
      d="M32.376 36.747v-.098c0-1.51.004-1.577 1.533-1.486.987.059 1.266-.231 1.21-1.218-.09-1.577-.024-1.58 1.525-1.58 1.732 0 1.776 0 1.727 1.77-.023.828.245 1.044 1.04 1.022 1.767-.046 1.768-.006 1.768 1.763 0 1.557-.004 1.629-1.6 1.534-1.024-.061-1.256.293-1.21 1.251.076 1.547.02 1.55-1.557 1.55-1.715 0-1.757-.003-1.7-1.705.027-.813-.123-1.14-1.035-1.095-1.699.082-1.702.02-1.702-1.707v-.001Z"
      fill="#FD6E5E"
    />
  </svg>
);

const Sidebar: FC = () => {
  const {
    sidebarSprings,
    toggleButtonSprings,
    handleOpenSidebar,
    handleOpenToggleButton,
    handleCloseSidebar,
    handleHideSidebar,
    handleCloseToggleButton,
    handleHideToggleButton,
    getIsSidebarOpen,
    getIsSidebarClosed,
    getIsSidebarHidden,
  } = useToggleSidebarAnimation();

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const isClosedBool = !isSidebarOpen;

  const { width } = useWindowSize();
  const { pathname } = useLocation();

  const syncSidebarState = (open: boolean) => {
    setIsSidebarOpen(open);
    if (open) {
      handleOpenSidebar();
      handleOpenToggleButton();
    } else if (width < 768) {
      handleHideSidebar();
      handleHideToggleButton();
    } else {
      handleCloseSidebar();
      handleCloseToggleButton();
    }
  };

  useEffect(() => {
    if (width < 768) {
      syncSidebarState(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, width]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: effect
  useEffect(() => {
    syncSidebarState(width >= 768);
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    width,
    handleOpenSidebar,
    handleOpenToggleButton,
    handleCloseSidebar,
    handleCloseToggleButton,
    handleHideToggleButton,
  ]);

  useGetUnreadMessages();

  useEffect(() => {
    if (width < 768 && getIsSidebarOpen()) {
      handleHideSidebar(); // Change to hide
      handleHideToggleButton();
    } else if (width >= 768 && (getIsSidebarClosed() || getIsSidebarHidden())) {
      handleOpenSidebar(); // Auto open or...
      handleOpenSidebar();
      handleOpenToggleButton();
    }
  }, [
    getIsSidebarClosed,
    getIsSidebarHidden,
    getIsSidebarOpen,
    handleHideSidebar,
    handleHideToggleButton,
    handleOpenSidebar,
    handleOpenToggleButton,
    width,
  ]);

  const handleToggleSidebar = () => {
    syncSidebarState(!isSidebarOpen);
  };

  const isMobileHidden = width < 768 && !isSidebarOpen;

  return (
    <animated.aside
      className="relative h-full overscroll-contain bg-white"
      style={sidebarSprings}
    >
      <div className="flex h-full flex-col gap-6 pt-4 font-semibold">
        <div className="relative px-5">
          {!isMobileHidden &&
            (isClosedBool ? (
              <LogoSmall height={30} width={30} />
            ) : (
              <LogoIcon height={30} width={82} />
            ))}
          <animated.div
            className="absolute -top-2 z-[99]"
            style={{ ...toggleButtonSprings }}
          >
            <Button
              aria-label="Toggle Sidebar"
              intent="ghost"
              leftIcon="chevron-left-filled"
              leftIconProps={{
                size: 40,
              }}
              onClick={handleToggleSidebar}
              shape="icon"
            />
          </animated.div>
        </div>
        {!isMobileHidden && (
          <nav className="flex flex-1 flex-col items-stretch gap-3 overflow-y-auto px-3 pb-8">
            <SidebarRoutes isClosed={isClosedBool} />
          </nav>
        )}
      </div>
    </animated.aside>
  );
};

export default Sidebar;
